import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { format } from "date-fns";
import { useState } from "react";
import { ModalPreviewDocument } from "../../../../shared/components/ModalPreviewDocument/ModalPreviewDocument";
import { ModalUploadDocument } from "./ModalUploadDocument";
import { IDocument } from "../types";
import { clientService } from "../../../../shared/services/api/client/clientService";
import { useToast } from "../../../../shared/hooks";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { translateDocumentType } from "../../utils/translate";

interface ModalDocumentListProps {
	isOpen: boolean;
	onClose: () => void;
	documents: IDocument[];
	clientId: string;
	title?: string;
	onDownloadAll?: () => void;
	onUploadSuccess?: (doc: IDocument | null) => void;
	onPreview?: (document: IDocument) => void;
	onDownload?: (document: IDocument) => void;
	isLoadingPreview?: boolean;
}

export const ModalDocumentList: React.FC<ModalDocumentListProps> = ({
	isOpen,
	onClose,
	documents,
	clientId,
	title = "Documentos Anexados",
	onDownloadAll,
	onUploadSuccess,
	onPreview,
	onDownload,
	isLoadingPreview = false,
}) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
	const [previewDoc, setPreviewDoc] = useState<IDocument | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const toastHandler = useToast();

	const handleClosePreview = () => {
		setPreviewDoc(null);
		setPreviewUrl(null);
	};

	const handleOpenUploadModal = () => setIsUploadModalOpen(true);
	const handleCloseUploadModal = () => setIsUploadModalOpen(false);

	const handleUploadFile = async (file: File, documentType: string) => {
		setIsLoading(true);
		try {
			const result = await clientService.uploadClientDocument(clientId, file, documentType);

			if (result instanceof Error) {
				throw result;
			}

			toastHandler.present({
				type: "success",
				position: "top-right",
				durationMs: 6000,
				messageOrContent: <ToastContent title="Documento enviado" content="O documento foi anexado com sucesso." />,
			});
			onUploadSuccess?.(result);
		} catch {
			toastHandler.present({
				type: "error",
				position: "top-right",
				durationMs: 6000,
				messageOrContent: <ToastContent title="Erro ao enviar documento" content="Não foi possível anexar o arquivo. Tente novamente." />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handlePreviewFile = async (doc: IDocument) => {
		setIsLoading(true);
		try {
			const blob = await clientService.previewClientDocument(doc.id);
			if (blob instanceof Error) throw blob;

			const fileURL = URL.createObjectURL(blob);
			setPreviewDoc(doc);
			setPreviewUrl(fileURL);
		} catch {
			toastHandler.present({
				type: "error",
				position: "top-right",
				durationMs: 6000,
				messageOrContent: <ToastContent title="Erro ao visualizar" content="Não foi possível abrir o documento." />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleRemoveFile = async (doc: IDocument) => {
		setIsLoading(true);
		try {
			const result = await clientService.removeClientDocument(doc.id);
			if (result instanceof Error) throw result;

			toastHandler.present({
				type: "success",
				position: "top-right",
				durationMs: 6000,
				messageOrContent: <ToastContent title="Documento removido" content="O documento foi removido com sucesso." />,
			});
			onUploadSuccess?.(null); // força refetch da lista
		} catch {
			toastHandler.present({
				type: "error",
				position: "top-right",
				durationMs: 6000,
				messageOrContent: <ToastContent title="Erro ao remover" content="Não foi possível remover o documento." />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Dialog open={isOpen} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h5" fontWeight={300} color="primary.main">
						{title}
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2}>
						<Box display="flex" justifyContent="end" alignItems="center">
							<Button variant="contained" size="small" startIcon={<UploadFileIcon />} onClick={handleOpenUploadModal}>
								Anexar Documento
							</Button>
						</Box>

						{documents?.length ? (
							<Stack spacing={2}>
								{documents.map((doc, index) => (
									<Paper key={index} variant="outlined" sx={{ px: 2, py: 1 }}>
										<Stack direction="row" justifyContent="space-between" alignItems="center">
											<Stack direction="row" spacing={1} flex={1}>
												<InsertDriveFileIcon fontSize="small" sx={{ color: "#999" }} />
												<Stack spacing={0.5}>
													<Typography variant="body2" fontWeight={500} lineHeight={1.2}>
														{doc.originalFilename}
													</Typography>
													<Typography variant="caption" color="text.secondary" lineHeight={1}>
														{format(new Date(doc.uploadedDate), "dd/MM/yyyy HH:mm")}
													</Typography>
													<Typography variant="subtitle2"  lineHeight={1}>
														{translateDocumentType(doc.docType ?? "")}
													</Typography>
												</Stack>
											</Stack>
											<Stack direction="row" spacing={0.5}>
												<Tooltip title="Visualizar">
													<IconButton size="small" onClick={() => handlePreviewFile(doc)}>
														<VisibilityIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Baixar">
													<IconButton size="small" onClick={() => window.open(doc.url, "_blank", "noopener,noreferrer")}>
														<DownloadIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Remover">
													<IconButton size="small" onClick={() => handleRemoveFile(doc)}>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</Stack>
										</Stack>
									</Paper>
								))}
							</Stack>
						) : (
							<Typography variant="body2">Nenhum documento anexado.</Typography>
						)}
					</Stack>
				</DialogContent>
			</Dialog>

			{!onPreview && previewDoc && (
				<ModalPreviewDocument
					open={!!previewDoc}
					onClose={handleClosePreview}
					previewDoc={previewDoc}
					previewUrl={previewUrl}
					isLoading={isLoadingPreview}
				/>
			)}

			<ModalUploadDocument
				isOpen={isUploadModalOpen}
				onClose={handleCloseUploadModal}
				nameDocument="novo documento"
				existingFileName={null}
				onAttach={async (file, documentType) => {
					await handleUploadFile(file, documentType);
					handleCloseUploadModal();
				}}
				onRemove={() => {}}
			/>

			<LoadingComp isopen={isLoading} />
		</>
	);
};
